import GoogleMap from './GoogleMap.js';
import { escapeAttr } from '../utility.js';

class SpotController {
  #currentView = 'card';
  #googleMapInstance = null;
  #cardContainer = null;
  #locationData = [];
  #filteredData = [];
  #isCardRendered = false;
  #cardCache = new Map(); // エリア別カードキャッシュ
  #dom = {};

  constructor() {
    this.#initializeDOMElements();
    this.#loadLocationData();
    this.#initializeGoogleMap(); // GoogleMapインスタンスを事前に生成（マップは未構築）
    this.#initializeEventListeners();
    this.#setInitialState();
  }

  #initializeDOMElements() {
    this.#dom.viewChanger = document.querySelector('[data-js="view-changer"]');
    this.#dom.mapElement = document.getElementById('map');
    this.#dom.cardElement = document.querySelector('.map-contents-card');
    this.#cardContainer = document.querySelector('.map-contents-card__lists');
    this.#dom.filterButtons = document.querySelectorAll('.map-filter-list__trigger');
  }

  #loadLocationData() {
    const locationScript = document.querySelector('[data-js="locations"]');
    if (locationScript) {
      this.#locationData = JSON.parse(locationScript.textContent);
      this.#filteredData = [...this.#locationData]; // 初期状態は全データ
    }
  }

  #initializeGoogleMap() {
    if (this.#dom.mapElement) {
      this.#googleMapInstance = new GoogleMap(this.#dom.mapElement, this);
    }
  }

  #initializeEventListeners() {
    // 表示切り替えボタン
    if (this.#dom.viewChanger) {
      this.#dom.viewChanger.addEventListener('click', (e) => {
        if (e.target.dataset.view) {
          this.switchView(e.target.dataset.view);
          this.#updateViewChangerState(e.target.dataset.view);
        }
      });
    }

    // フィルターボタンのイベントリスナーを登録
    // マップ初期表示前（カード表示中）にもフィルター操作を可能にし、
    // マップ表示後はGoogleMapクラスのクリック処理を優先する
    this.#dom.filterButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        if (this.#googleMapInstance && this.#googleMapInstance.getMap()) {
          // マップがロード済みの場合はGoogleMap側で処理
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        const clickedArea = button.dataset.area;
        const isCurrentlyChecked = button.dataset.checked === 'true';
        
        if (clickedArea === 'all' || isCurrentlyChecked) {
          this.#dom.filterButtons.forEach(btn => {
            btn.dataset.checked = 'false';
          });
          this.filterByArea('default', button);
        } else {
          this.#dom.filterButtons.forEach(btn => {
            btn.dataset.checked = 'false';
          });
          button.dataset.checked = 'true';
          this.filterByArea(clickedArea, button);
        }
      });
    });
  }

  #setInitialState() {
    // 初期状態はカード表示
    this.#dom.cardElement.style.display = 'block';
    this.#dom.mapElement.style.display = 'none';
    this.#currentView = 'card';
    
    // 初期の表示切り替えボタン状態
    const cardButton = this.#dom.viewChanger?.querySelector('[data-view="card"]');
    if (cardButton) {
      cardButton.classList.add('is-active');
    }

    // カードのレンダリングと時差表示アニメーションを実行
    this.#renderCards();
    requestAnimationFrame(() => this.#animateCards());
  }

  // 表示切り替えメソッド
  switchView(viewType) {
    // ビュー切り替え時に詳細ドロワー（モーダル）を閉じる
    if (this.#googleMapInstance) {
      this.#googleMapInstance.closeInformationPanel();
    }

    if (viewType === 'map') {
      this.#dom.mapElement.style.display = 'block';
      this.#dom.cardElement.style.display = 'none';
      this.#currentView = 'map';
      
      // GoogleMapの遅延初期化（地図の構築を実行）
      if (this.#googleMapInstance) {
        this.#googleMapInstance.initMap();
      }
      
      if (window.google && this.#googleMapInstance.getMap()) {
        // Google Mapのリサイズトリガー
        setTimeout(() => {
          google.maps.event.trigger(this.#googleMapInstance.getMap(), 'resize');
        }, 100);
      }
    } else if (viewType === 'card') {
      this.#dom.mapElement.style.display = 'none';
      this.#dom.cardElement.style.display = 'block';
      this.#currentView = 'card';
      
      // カード表示時のみレンダリング
      this.#renderCards();
      // 毎回アニメーションを実行
      requestAnimationFrame(() => this.#animateCards());
    }
  }

  // GoogleMapクラスからの通知を受けるメソッド
  updateFilterState(targetArea, buttonElement) {
    console.log('SpotController: updateFilterState called with area:', targetArea); // デバッグログ
    
    // データフィルタリング
    if (targetArea === 'default' || !targetArea) {
      this.#filteredData = [...this.#locationData];
    } else {
      this.#filteredData = this.#locationData.filter(location => 
        location.area === targetArea
      );
    }

    // カード表示の場合のみ更新
    if (this.#currentView === 'card') {
      this.#isCardRendered = false; // 再レンダリングフラグをリセット
      this.#renderCards();
      requestAnimationFrame(() => this.#animateCards());
    }
  }

  // エリアフィルター
  filterByArea(targetArea, buttonElement) {
    // ボタンの状態更新
    this.#updateFilterButtonState(buttonElement);

    // データフィルタリング
    if (targetArea === 'default' || !targetArea) {
      this.#filteredData = [...this.#locationData];
    } else {
      this.#filteredData = this.#locationData.filter(location => 
        location.area === targetArea
      );
    }

    // 現在の表示に応じて更新
    if (this.#currentView === 'map') {
      // GoogleMapクラスのfilterMarkersメソッドは呼び出さない
      // フィルターボタンのクリックイベントがGoogleMapクラスで直接処理される
    } else {
      this.#isCardRendered = false; // 再レンダリングフラグをリセット
      this.#renderCards();
      requestAnimationFrame(() => this.#animateCards());
    }
  }

  // カードレンダリング
  #renderCards() {
    // 既存のカードを削除
    this.#cardContainer.innerHTML = '';

    // JSONデータからカードを動的生成
    this.#filteredData.forEach((location, index) => {
      const cardElement = this.#createCardElement(location, index);
      this.#cardContainer.appendChild(cardElement);
    });

    this.#isCardRendered = true;
  }

  // カード要素作成
  #createCardElement(location, index) {
    const li = document.createElement('li');
    li.className = 'map-contents-card-list';
    li.dataset.area = location.area;
    li.style.opacity = '0';
    li.style.transform = 'translateY(20px)';
    li.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    li.style.cursor = 'pointer';
    
    li.innerHTML = `
      <figure class="map-contents-card-list__image">
        <span class="c-clip">
          <img src="${location.thumbnail}" 
            width="260" height="339" 
            alt="${escapeAttr(location.title)}" 
            loading="lazy">
        </span>
      </figure>
      `;
      // <div class="map-contents-card-list__info">
      //   <h3 class="map-contents-card-list__title">${location.title}</h3>
      //   ${location.description ? `<p class="map-contents-card-list__description">${location.description}</p>` : ''}
      // </div>
    
    // カードクリック時のイベントリスナーを追加
    li.addEventListener('click', () => {
      if (this.#googleMapInstance) {
        this.#googleMapInstance.showInformationPanel(location);
      }
    });
    
    return li;
  }

  // カードアニメーション
  #animateCards() {
    const cards = this.#cardContainer.querySelectorAll('.map-contents-card-list');
    
    // 全カードを初期状態にリセット
    cards.forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
    });

    // 時差アニメーションで表示
    cards.forEach((card, index) => {
      setTimeout(() => {
        card.style.opacity = '1';
        card.style.transform = 'translateY(0)';
      }, index * 100); // 100ms間隔で順次表示
    });
  }

  // 表示切り替えボタンの状態更新
  #updateViewChangerState(activeView) {
    const buttons = this.#dom.viewChanger?.querySelectorAll('[data-view]');
    if (buttons) {
      buttons.forEach(button => {
        button.classList.toggle('is-active', button.dataset.view === activeView);
      });
    }
  }

  // フィルターボタンの状態更新
  #updateFilterButtonState(activeButton) {
    this.#dom.filterButtons.forEach(button => {
      if (button === activeButton) {
        const isChecked = button.dataset.checked === 'true';
        button.dataset.checked = String(!isChecked);
      } else {
        button.dataset.checked = 'false';
      }
    });
  }

  // 現在のフィルターキーを取得
  getCurrentFilterKey() {
    const activeButton = document.querySelector('.map-filter-list__trigger[data-checked="true"]');
    return activeButton ? activeButton.dataset.area : 'default';
  }

  // 公開メソッド
  getCurrentView() {
    return this.#currentView;
  }

  getLocationData() {
    return this.#locationData;
  }

  getFilteredData() {
    return this.#filteredData;
  }

  // GoogleMapインスタンスを取得
  getGoogleMapInstance() {
    return this.#googleMapInstance;
  }

}

export default SpotController;
